import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

const TYPE_COLORS = {
  chat:     "bg-teal-100 text-teal-800",
  workshop: "bg-purple-100 text-purple-800",
  offer:    "bg-blue-100 text-blue-800",
  booking:  "bg-green-100 text-green-800",
  akutt:    "bg-red-100 text-red-800",
};

// Viser hvor en booking kom fra når mekaniker har godkjent/booket den fra en
// akutt- eller chat-henvendelse (se createdBy.source).
const ORIGIN_COLORS = {
  Akutt: "bg-red-50 text-red-700 border border-red-200",
  Chat: "bg-teal-50 text-teal-700 border border-teal-200",
};

const SOURCES = [
  { value: "alle",     label: "Alle" },
  { value: "chat",     label: "Chat" },
  { value: "akutt",    label: "Akutt" },
  { value: "workshop", label: "Verksted" },
  { value: "offer",    label: "Pristilbud" },
  { value: "booking",  label: "Bestilling" },
];

const fmtDT = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleString("no-NO", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

export default function ChatHistorikk() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [sok, setSok] = useState("");
  const [source, setSource] = useState("alle");
  const [expanded, setExpanded] = useState({});
  const [markingUnread, setMarkingUnread] = useState({});
  const [markedDone, setMarkedDone] = useState({});

  const debounceRef = useRef(null);
  const [debouncedSok, setDebouncedSok] = useState("");

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSok(sok);
      setPage(1);
    }, 350);
  }, [sok]);

  const fetchHistorikk = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page, source });
      if (debouncedSok) params.set("sok", debouncedSok);

      const r = await fetch(`${API_BASE_URL}/notifications/historikk?${params}`, { cache: "no-store" });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch {
      setError("Kunne ikke hente historikk.");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSok, source]);

  useEffect(() => { fetchHistorikk(); }, [fetchHistorikk]);

  const UNREAD_ENDPOINTS = {
    chat:     (id) => [`${API_BASE_URL}/chat/${id}/mark-unread`, "PATCH"],
    akutt:    (id) => [`${API_BASE_URL}/akutt/${id}/mark-unread`, "PATCH"],
    workshop: (id) => [`${API_BASE_URL}/workshop-services/${id}/mark-unread`, "POST"],
    offer:    (id) => [`${API_BASE_URL}/offers/${id}/mark-unread`, "POST"],
    booking:  (id) => [`${API_BASE_URL}/bookings/${id}/mark-unread`, "POST"],
  };

  async function handleMarkUnread(item) {
    const key = `${item.type}-${item.id}`;
    setMarkingUnread((p) => ({ ...p, [key]: true }));
    try {
      const [url, method] = UNREAD_ENDPOINTS[item.type](item.id);
      await fetch(url, { method });
      setMarkedDone((p) => ({ ...p, [key]: true }));
    } catch { /* silent */ } finally {
      setMarkingUnread((p) => ({ ...p, [key]: false }));
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Samtalehistorikk</h1>
          <p className="text-sm text-gray-500 mt-0.5">Alle meldinger fra portaler · {total} totalt</p>
        </div>
        <Link to="/varsling" className="text-sm text-blue-600 hover:underline">
          ← Til varsling
        </Link>
      </div>

      {/* Filtre */}
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          placeholder="Søk på navn, telefon eller ref…"
          value={sok}
          onChange={(e) => setSok(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
        />
        <select
          value={source}
          onChange={(e) => { setSource(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
        >
          {SOURCES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">Laster historikk…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center">
          <p className="text-gray-500">Ingen samtaler funnet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const isExpanded = !!expanded[item.id];
            const msgCount = (item.messages || []).length;
            const customerMsgs = item.messages.filter((m) => m.from === "customer").length;

            return (
              <div
                key={`${item.type}-${item.id}`}
                className="rounded-xl border border-gray-200 shadow-sm overflow-hidden bg-white"
              >
                {/* Kortlinje */}
                <button
                  type="button"
                  className="w-full text-left px-4 py-3 flex flex-wrap items-start justify-between gap-x-3 gap-y-1"
                  onClick={() => setExpanded((p) => ({ ...p, [item.id]: !p[item.id] }))}
                >
                  <div className="flex min-w-0 items-center gap-2 flex-wrap">
                    <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${TYPE_COLORS[item.type] || "bg-gray-100 text-gray-700"}`}>
                      {item.typeLabel}
                    </span>
                    {item.origin && (
                      <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${ORIGIN_COLORS[item.origin] || "bg-gray-100 text-gray-600 border border-gray-200"}`}>
                        fra {item.origin}
                      </span>
                    )}
                    {item.ref && (
                      <span className="text-xs text-gray-400 font-mono">#{item.ref}</span>
                    )}
                    <span className="font-semibold text-gray-900 text-sm">{item.customerName}</span>
                    {item.customerPhone && (
                      <a
                        href={`tel:${item.customerPhone}`}
                        className="text-xs text-blue-600 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.customerPhone}
                      </a>
                    )}
                    {(item.type === "chat" || item.type === "akutt") && (
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                        item.besvart ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                      }`}>
                        {item.besvart ? "Besvart" : "Ubesvart"}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-gray-400">{customerMsgs}/{msgCount} meld.</span>
                    <span className="text-xs text-gray-400">{fmtDT(item.createdAt)}</span>
                    <span className="text-gray-400 text-sm">{isExpanded ? "▲" : "▼"}</span>
                  </div>
                </button>

                {/* Utvidet tråd */}
                {isExpanded && (
                  <div className="border-t border-gray-100 px-4 py-4 space-y-3">
                    {/* Handlinger */}
                    <div className="flex justify-end">
                      {markedDone[`${item.type}-${item.id}`] ? (
                        <span className="text-xs text-green-600 font-medium">Merket som ulest ✓</span>
                      ) : (
                        <button
                          type="button"
                          disabled={!!markingUnread[`${item.type}-${item.id}`]}
                          onClick={() => handleMarkUnread(item)}
                          className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                          {markingUnread[`${item.type}-${item.id}`] ? "…" : "Merk som ulest"}
                        </button>
                      )}
                    </div>
                    {/* Meldinger */}
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                    {item.messages.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-2">Ingen meldinger</p>
                    ) : (
                      item.messages.map((m, i) => (
                        <div
                          key={i}
                          className={`flex ${m.from === "customer" ? "justify-start" : "justify-end"}`}
                        >
                          <div
                            className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                              m.from === "customer"
                                ? "bg-gray-100 text-gray-800"
                                : item.type === "chat"
                                  ? "bg-teal-600 text-white"
                                  : item.type === "akutt"
                                    ? "bg-red-600 text-white"
                                    : item.type === "workshop"
                                      ? "bg-purple-600 text-white"
                                      : item.type === "offer"
                                        ? "bg-blue-600 text-white"
                                        : "bg-green-600 text-white"
                            }`}
                          >
                            <p className="leading-snug">{m.text}</p>
                            <p className={`text-[10px] mt-1 opacity-70`}>
                              {m.fromName} · {fmtDT(m.createdAt)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Paginering */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            ← Forrige
          </button>
          <span className="text-sm text-gray-500">Side {page} av {pages}</span>
          <button
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            Neste →
          </button>
        </div>
      )}
    </div>
  );
}
